/**
 * seatingMoves.js — deterministic, client-side evaluation of a seating MOVE.
 *
 * A "move" = relocating one or more guests to a target table. These are PURE functions
 * (no network, no React) so they can be unit-tested and reused everywhere a move happens:
 *   • drag a seated guest from one table to another on the 2D floor
 *   • drag a whole multi-selected family/group as a unit
 *   • change a guest's table inline from the "Seated" roster
 *   • the ▦ Seats numbered grid
 *
 * This module only decides what a move WOULD do and what the host should be warned about.
 * The actual persistence still happens through the governed PATCH /seating/assignments
 * endpoint in the page (Guests stays the source of truth). Nothing here writes.
 *
 * Severity model (mirrors the solver's hard/soft rules):
 *   • capacity overflow      → BLOCKING (must resolve: drop movers or bump occupants)
 *   • must-separate conflict → BLOCKING (two guests who can't share a table)
 *   • accessibility          → BLOCKING (an accessibility-needs guest at a non-accessible table)
 *   • family split           → WARNING  (host may split a family on purpose — confirm, don't block)
 */

const num = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
const cap = (t) => Math.max(0, num(t && t.seat_count, 8));
const needsAccess = (g) => !!(g && (g.accessibility_needs || /wheel|access|mobility/i.test(String(g.accessibility_label || ''))));
export const isChild = (g) => !!(g && g.age_group && /child|kid|infant|minor/i.test(String(g.age_group)));

/** Index the pool + current assignments into fast lookups. */
export function indexSeating({ tables, assignments, poolList }) {
  const guestsById = {};
  (poolList || []).forEach((g) => { if (g && g.id) guestsById[g.id] = g; });
  const tablesById = {};
  (tables || []).forEach((t) => { if (t && t.id) tablesById[t.id] = t; });
  const tableOf = {};         // guest_id -> table_id
  const protectedOf = {};     // guest_id -> bool
  const occByTable = {};      // table_id -> [guest_id]
  (assignments || []).forEach((a) => {
    if (!a || !a.guest_id) return;
    if (a.table_id) {
      tableOf[a.guest_id] = a.table_id;
      (occByTable[a.table_id] = occByTable[a.table_id] || []).push(a.guest_id);
    }
    protectedOf[a.guest_id] = !!a.protected;
  });
  return { guestsById, tablesById, tableOf, protectedOf, occByTable };
}

/** The party key used to keep a nuclear unit together. party_id is the only HARD key. */
export function partyKeyOf(g) {
  if (!g) return null;
  if (g.party_id) return `pty:${g.party_id}`;
  return null;
}

/**
 * The ONE chair-map used by every surface (2D canvas, 3D room, seat grid, printed chart):
 * per table, an array of length seat_count placing each guest at their SEAT INDEX (1-based).
 * Guests whose seat_index is missing/invalid/colliding fall into the first open chair, so
 * every seated guest always renders on exactly one real chair.
 */
export function buildSeatMap(tables, assignments) {
  const m = {};
  (tables || []).forEach((t) => { if (t && t.id) m[t.id] = new Array(Math.max(0, num(t.seat_count, 8))).fill(null); });
  (assignments || []).forEach((a) => {
    if (!a || !a.table_id) return; const arr = m[a.table_id]; if (!arr) return;
    let idx = a.seat_index ? a.seat_index - 1 : -1;
    if (idx < 0 || idx >= arr.length || arr[idx]) idx = arr.findIndex((x) => x === null);
    if (idx >= 0) arr[idx] = a.guest_id;
  });
  return m;
}

/**
 * Evaluate moving `movingIds` to `targetTableId`.
 * Returns a structured analysis the resolver UI and the heads-up banner consume.
 */
export function evaluateMove({ movingIds, targetTableId, tables, assignments, poolList }) {
  const idx = indexSeating({ tables, assignments, poolList });
  const { guestsById, tablesById, tableOf, protectedOf, occByTable } = idx;
  const target = tablesById[targetTableId] || null;

  const moving = (movingIds || []).filter((id) => guestsById[id]);
  // Movers that actually change location (already-at-target guests are no-ops).
  const movers = moving.filter((id) => tableOf[id] !== targetTableId);
  const currentOcc = (occByTable[targetTableId] || []).filter((id) => !movers.includes(id));

  const capacity = (() => {
    const seats = cap(target);
    const open = Math.max(0, seats - currentOcc.length);
    const needed = movers.length;
    const overflow = Math.max(0, needed - open);
    return { seats, open, needed, overflow, ok: overflow === 0 };
  })();

  // Resulting occupant set if the move went through (ignoring capacity for analysis).
  const resulting = currentOcc.concat(movers);
  const nameOf = (id) => (guestsById[id] || {}).name || id;

  // must-separate conflicts among the resulting occupants (symmetric — check both directions).
  const conflicts = [];
  for (let i = 0; i < resulting.length; i += 1) {
    for (let j = i + 1; j < resulting.length; j += 1) {
      const a = guestsById[resulting[i]] || {};
      const b = guestsById[resulting[j]] || {};
      const aw = a.conflicts_with || [];
      const bw = b.conflicts_with || [];
      if (aw.includes(b.id) || bw.includes(a.id)) {
        conflicts.push({ a: { id: a.id, name: a.name }, b: { id: b.id, name: b.name } });
      }
    }
  }

  // accessibility — an accessibility-needs guest landing at a non-accessible table.
  const accessibility = [];
  if (target && !target.accessible) {
    movers.forEach((id) => { if (needsAccess(guestsById[id])) accessibility.push({ id, name: nameOf(id) }); });
  }

  // family split — would any mover's party end up across more than one table?
  const familySplits = [];
  const partiesTouched = new Set(movers.map((id) => partyKeyOf(guestsById[id])).filter(Boolean));
  partiesTouched.forEach((pk) => {
    // every pool guest in this party + where they'd sit after the move
    const members = (poolList || []).filter((g) => partyKeyOf(g) === pk);
    if (members.length < 2) return;
    const tableAfter = (gid) => (movers.includes(gid) ? targetTableId : (tableOf[gid] || null));
    const seatedTables = new Set(members.map((g) => tableAfter(g.id)).filter(Boolean));
    const atTarget = members.filter((g) => tableAfter(g.id) === targetTableId).length;
    if (seatedTables.size > 1) {
      familySplits.push({
        partyKey: pk,
        name: members[0].household || members[0].name || 'family',
        atTarget,
        total: members.length,
        tables: Array.from(seatedTables).map((tid) => (tablesById[tid] || {}).label || 'unseated'),
      });
    }
  });

  // Who could be bumped to make room — current occupants, ranked easiest-first:
  // unprotected before protected; solo guests before family members; non-children, then by name.
  const bumpCandidates = currentOcc
    .map((id) => {
      const g = guestsById[id] || {};
      return {
        id,
        name: g.name || id,
        protected: !!protectedOf[id],
        party: partyKeyOf(g),
        child: isChild(g),
      };
    })
    .sort((x, y) => (x.protected - y.protected)
      || ((x.party ? 1 : 0) - (y.party ? 1 : 0))
      || (x.child - y.child)
      || String(x.name).localeCompare(String(y.name)));

  const blocking = !capacity.ok || conflicts.length > 0 || accessibility.length > 0;
  const warnings = [];
  if (familySplits.length) warnings.push({ type: 'family_split', items: familySplits });

  const summaryParts = [];
  if (!capacity.ok) summaryParts.push(`${capacity.needed} moving · only ${capacity.open} open seat${capacity.open === 1 ? '' : 's'}`);
  if (conflicts.length) summaryParts.push(`${conflicts.length} must-separate conflict${conflicts.length === 1 ? '' : 's'}`);
  if (accessibility.length) summaryParts.push(`${accessibility.length} accessibility issue${accessibility.length === 1 ? '' : 's'}`);
  if (familySplits.length) summaryParts.push(`${familySplits.length} family would split`);

  return {
    target,
    movers,                  // ids that actually change tables
    noChange: moving.length > 0 && movers.length === 0,
    capacity,                // { seats, open, needed, overflow, ok }
    conflicts,               // [{a,b}] BLOCKING
    accessibility,           // [{id,name}] BLOCKING
    familySplits,            // [{partyKey,name,atTarget,total,tables}] WARNING
    bumpCandidates,          // current occupants, easiest-to-bump first
    blocking,                // must be resolved/confirmed before applying
    warnings,                // soft advisories
    clean: !blocking && warnings.length === 0,
    summary: summaryParts.join(' · '),
  };
}

/**
 * Suggest tables a guest/group could be relocated to: enough open seats AND no NEW must-separate
 * conflict introduced. Used by the resolver's "move them to…" picker. Ranked by best fit
 * (tightest table that still fits, accessible-first if the group needs it).
 */
export function suggestTablesFor(ids, { tables, assignments, poolList, excludeTableId }) {
  const idx = indexSeating({ tables, assignments, poolList });
  const { guestsById, occByTable } = idx;
  const group = (ids || []).filter((id) => guestsById[id]);
  const groupNeedsAccess = group.some((id) => needsAccess(guestsById[id]));
  const out = [];
  (tables || []).forEach((t) => {
    if (t.id === excludeTableId) return;
    const occ = (occByTable[t.id] || []).filter((id) => !group.includes(id));
    const open = Math.max(0, cap(t) - occ.length);
    if (open < group.length) return;
    // no new conflict
    const resulting = occ.concat(group);
    let conflict = false;
    for (let i = 0; i < resulting.length && !conflict; i += 1) {
      for (let j = i + 1; j < resulting.length && !conflict; j += 1) {
        const a = guestsById[resulting[i]] || {}; const b = guestsById[resulting[j]] || {};
        if ((a.conflicts_with || []).includes(b.id) || (b.conflicts_with || []).includes(a.id)) conflict = true;
      }
    }
    if (conflict) return;
    out.push({
      id: t.id,
      label: t.label || 'Table',
      open,
      accessible: !!t.accessible,
      fit: open - group.length, // smaller = tighter = preferred
    });
  });
  out.sort((x, y) => (groupNeedsAccess ? (y.accessible - x.accessible) : 0) || (x.fit - y.fit) || String(x.label).localeCompare(String(y.label)));
  return out;
}

/**
 * Milo concierge "Suggest a table" (host 2026-07-13): for an ISOLATED guest, rank the tables
 * where they actually KNOW someone — same household, same group_name, or a shared relationship
 * group. Grounded only in real guest data; excludes their current table, tables with no free
 * seat, and tables where the move would introduce a must-separate conflict or break
 * accessibility (reuses suggestTablesFor's safety filter). Returns up to `max` candidates:
 * [{tableId, label, open, connections, names}] sorted by most-connections, then most room.
 */
export function suggestConnectedTables(guestId, { tables, assignments, poolList, relGroups, max = 3 }) {
  const idx = indexSeating({ tables, assignments, poolList });
  const { guestsById, tableOf, occByTable } = idx;
  const g = guestsById[guestId];
  if (!g) return [];
  const circles = new Set();
  (relGroups || []).forEach((grp) => { if ((grp.guest_ids || []).includes(guestId)) circles.add(grp.id || grp.name); });
  const knows = (other) => !!(other && other.id !== guestId && (
    (g.household && g.household === other.household)
    || (g.group_name && g.group_name === other.group_name)
    || (relGroups || []).some((grp) => circles.has(grp.id || grp.name) && (grp.guest_ids || []).includes(other.id))
  ));
  const safe = new Map(suggestTablesFor([guestId], { tables, assignments, poolList, excludeTableId: tableOf[guestId] })
    .map((t) => [t.id, t]));
  const out = [];
  safe.forEach((t, tid) => {
    const mates = (occByTable[tid] || []).map((id) => guestsById[id]).filter(knows);
    if (!mates.length) return;
    out.push({ tableId: tid, label: t.label, open: t.open, connections: mates.length, names: mates.map((m) => m.name).slice(0, 4) });
  });
  out.sort((a, b) => (b.connections - a.connections) || (b.open - a.open) || String(a.label).localeCompare(String(b.label)));
  return out.slice(0, max);
}
